import React, { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import Skeleton from "react-loading-skeleton";
import Icon from "components/Icon";
import SearchInput from "components/SearchInput";
import HighlightTarget from "components/HighlightTarget";

const TableNew = ({
  data, // array of things to be displayed in the table
  columns, // array of how those things are to be displayed in the table -- sortable? header? specially rendered stuff? etc.
  defaultSortKey, // of those columns, which key are we using for the initial sort
  searchableKeys = [], // of those columns which keys should be allowed to sort
  fullWidthSearch = true, // show search input in full width unless it set false or table header avaiable
  multiSelect = false, // can the user select multiple rows?
  singleSelect = false, // can the user select only a single row at a time?
  includeSelectAllOption = false, // at the top of the list will be an "All" option which will trigger none of the options to be selected (and thus all)
  selectionReference = "uuid", // when selecting rows what should be used as the reference property on each thing in the data array
  defaultSortOrder = "asc",
  onSelectionChange = () => { }, // you can optionally trigger something to happen on every single select action (or wait and do it once the user applies/cancels)
  loading = false, // if we need before the data has been fetched
  emptyMessageKey = "comp.sitePerformance.noData",
  showHeaders = true,
  icon = false,
  preSelectedRows = [], // in the case of one or more of the things being "preselected" (in many cases this might be "All")
  requireSelection = false, // prevent deselection when singleSelect is true (forces at least one item to stay selected)
  tableView = false,
  tableHeader,
  hasMoreData,
  onLoadMore,
  scrollFullTable = false,
  maxHeight,
  rowClickAction,
  headerBackgroundColor = "",
  headerSlim = false,
  noPadding = false
}) => {
  const sentinelRef = useRef(null);
  const intl = useIntl();
  const debounceRef = useRef(false);
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState(data || []);
  const [selectedRows, setSelectedRows] = useState(preSelectedRows);
  const isAllSelected = selectedRows === "all";
  const headerStyle = {
    ...(headerBackgroundColor && headerBackgroundColor.trim() !== '' && {
      backgroundColor: `var(${headerBackgroundColor})`,
    }),
  };

  useEffect(() => {
    if (preSelectedRows.length && selectedRows.length === 0) {
      setSelectedRows(preSelectedRows);
    }
  }, [preSelectedRows]);

  useEffect(() => {
    if (!searchText) {
      setFilteredData(data || []);
    }
  }, [data, searchText]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel || loading || !hasMoreData) return;

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !debounceRef.current) {
          debounceRef.current = true;
          onLoadMore();

          // Release lock after short delay to let Redux update
          setTimeout(() => {
            debounceRef.current = false;
          }, 400); // Adjust for backend response speed
        }
      });

      observer.observe(sentinel);

      return () => observer.disconnect();
    }, 50);

    return () => clearTimeout(timeout);
  }, [hasMoreData, loading, onLoadMore]);

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  };

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!filteredData) return [];

    return [...filteredData].sort((a, b) => {
      const aVal = getNestedValue(a, sortKey);
      const bVal = getNestedValue(b, sortKey);

      const aEmpty = aVal == null || aVal === "";
      const bEmpty = bVal == null || bVal === "";

      // Push empty values to the end
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;

      // Regular sorting
      if (typeof aVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filteredData, sortKey, sortOrder]);

  // Handle sorting clicks
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleSelect = (row) => {
    const reference = row[selectionReference];
    let newSelection;

    if (multiSelect) {
      if (selectedRows === "all") {
        newSelection = [reference];
      } else {
        const alreadySelected = selectedRows.includes(reference);
        newSelection = alreadySelected
          ? selectedRows.filter((r) => r !== reference)
          : [...selectedRows, reference];
      }
    } else if (singleSelect) {
      const alreadySelected = selectedRows[0] === reference;
      
      // If requireSelection is true and the row is already selected, don't deselect it
      if (requireSelection && alreadySelected) {
        return; // Do nothing - keep it selected
      }
      
      newSelection = alreadySelected ? [] : [reference];
    } else {
      return;
    }

    setSelectedRows(newSelection);
    onSelectionChange(newSelection);
  };

  const selectAll = () => {
    setSelectedRows("all");
    onSelectionChange("all");
  };

  const showSkeleton = loading;
  const showEmpty = !loading && sortedData.length === 0;

  const WrapperElement = scrollFullTable ? 'div' : Fragment;
  const wrapperProps = scrollFullTable ? { className: 'overflow-x-auto custom-scrollbar' } : {};

  return (
    <>
      <div className="flex justify-between gap-4 w-full min-w-0 items-center">
        {tableHeader && <h2 className="tw-heading-sm m-0 flex flex-row gap-2 items-center flex-1">{tableHeader}</h2>}
        {searchableKeys.length > 0 &&
          <SearchInput
            id="search-input-table"
            dataCy="search-input-table"
            placeholder={intl.formatMessage({ id: 'comp.search.default' })}
            data={data}
            filterKeys={searchableKeys}
            onResults={setFilteredData}
            value={searchText}
            onChange={(v) => setSearchText(v)}
            className={tableHeader || fullWidthSearch === false ? "" : "flex-1"}
          />
        }
      </div>

      <WrapperElement {...wrapperProps}>
        {/* Have to break headers out into their own table for lots of reasons...mostly layout and alignment and so they can stick */}
        {showHeaders && <div className={classNames(maxHeight ? maxHeight + " pr-1" : "")}>
          <table className="w-full table-fixed">
            <thead className="tw-paragraph-sm font-semibold uppercase text-[var(--color-gray-400)] !border-spacing-0"
              style={headerStyle}
            >
              <tr>
                {icon && <th className="p-2" style={{ width: 56 }}></th>}
                {columns.map((col, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === columns.length - 1;
                  return (
                    <th
                      key={idx}
                      onClick={() => col.sortable && handleSort(col.key)}
                      className={classNames(
                        (tableView || showHeaders) && headerSlim ? "py-2" : "pt-6 pb-4",
                        col.sortable && "cursor-pointer",
                        (tableView || showHeaders) && isFirst && !noPadding && "pl-8",
                        (tableView || showHeaders) && isLast && !noPadding && "pr-8",
                        (tableView || showHeaders) && !isFirst && !noPadding && "pl-2",
                        (tableView || showHeaders) && isFirst && headerSlim ? `rounded-l-4xl` : "",
                        (tableView || showHeaders) && isLast && headerSlim ? `rounded-r-4xl` : "",
                      )}
                      style={col.width ? { width: col.width } : {}}
                    >
                      <div className={classNames(
                        "flex items-center gap-1",
                        col.align && col.align
                      )}>
                        {col.label && (
                          col.highlightTarget ? (
                            <HighlightTarget id={col.highlightTarget}>
                              {col.label}
                            </HighlightTarget>
                          ) : (
                            col.label
                          )
                        )}
                        {col.sortable && sortKey === col.key && (
                          <Icon
                            name={sortOrder === "asc" ? "chevronUp" : "chevronDown"}
                            size={16}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>}

        {/* Main Table */}
        <div className={classNames(
          !scrollFullTable ? "overflow-auto custom-scrollbar" : "",
          maxHeight ? `${maxHeight} pr-1` : ""
        )}>
          <table className="w-full tw-paragraph-reg table-fixed border-separate border-spacing-y-1">
            <tbody>
              {/* Skeleton while loading */}
              {showSkeleton &&
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    {columns.map((col, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === columns.length - 1;
                      return (
                        <td key={idx} className="p-0">
                          <div
                            className={classNames(
                              "flex items-center w-full min-h-14 font-outfit bg-white rounded-md",
                              isFirst && !noPadding && "pl-8",
                              isLast && !noPadding && "pr-8",
                              !isFirst && !noPadding && "p1-2",
                              col.align && col.align
                            )}
                          >
                            <Skeleton
                              height={24}
                              width="75%"
                              containerClassName={classNames(
                                "flex flex-1",
                                col.align && col.align
                              )}
                            />
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}

              {/* Select All option */}
              {!showSkeleton && includeSelectAllOption && multiSelect && (
                <tr
                  className={classNames(
                    "transition-colors cursor-pointer",
                    isAllSelected && "bg-gray-300",
                    !isAllSelected && "hover:bg-gray-100"
                  )}
                  onClick={selectAll}
                >
                  {icon && <td className="p-2" style={{ width: 56 }}>
                    {icon}
                  </td>}
                  <td>
                    <div
                      className={classNames(
                        "flex items-center break-words capitalize font-outfit w-full min-h-14",
                        tableView && "bg-white",
                        !noPadding && "pl-8 pr-8",
                        "rounded-l-[12px] rounded-r-[12px]"
                      )}
                    >
                      <p className="tw-paragraph-reg font-semibold">All</p>
                    </div>
                  </td>
                </tr>
              )}

              {/* Regular data to be displayed */}
              {!showSkeleton &&
                sortedData.map((item, rowIdx) => {
                  const isSelected = selectedRows.includes(item[selectionReference]);
                  return (
                    <tr
                      key={rowIdx}
                      className={classNames(
                        "transition-colors",
                        (singleSelect || multiSelect || rowClickAction) && "cursor-pointer",
                        (singleSelect || multiSelect) && !isSelected && "hover:bg-gray-100",
                        (singleSelect || multiSelect) && isSelected && "bg-gray-300",
                      )}
                      onClick={(e) => { (singleSelect || multiSelect) && handleSelect(item); rowClickAction && rowClickAction(item, e) }}
                    >
                      {icon && <td className="p-2" style={{ width: 56 }}>
                        {icon}
                      </td>}
                      {columns.map((col, colIdx) => {
                        const isFirst = colIdx === 0;
                        const isLast = colIdx === columns.length - 1;
                        return (
                          <td
                            key={colIdx}
                            data-cy={`${col.key}-${rowIdx}`}
                            className={classNames(
                              "p-0",
                              col.truncate ? "max-w-0" : "break-words whitespace-normal",
                              !col.width && "w-full"
                            )}
                            style={col.width ? { width: col.width } : {}}
                          >
                            <div
                              className={classNames(
                                "flex items-center font-outfit w-full min-h-14 min-w-0",
                                !col.truncate && "break-words whitespace-normal",
                                tableView && "bg-white",
                                isFirst && !noPadding && "pl-8 rounded-l-[12px]",
                                isLast && !noPadding && "pr-8 rounded-r-[12px]",
                                !isFirst && !noPadding && "pl-2",
                                col.align && col.align
                              )}
                            >
                              {col.truncate ? (
                                <span 
                                  className="truncate block min-w-0 flex-1"
                                  title={!col.render ? String(item[col.key] || '') : undefined}
                                >
                                  {col.render ? col.render(item) : item[col.key]}
                                </span>
                              ) : (
                                col.render ? col.render(item) : item[col.key]
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

              {/* Loading sentinel if we want infinite scroll */}
              {!showSkeleton && !showEmpty && sortedData.length && tableView && <tr ref={sentinelRef}>
                <td colSpan={columns.length} className="text-center">
                  {hasMoreData && <p className="tw-paragraph-sm text-gray-400 py-6">{intl.formatMessage({ id: "common.loadingMore" })}</p>}
                </td>
              </tr>}

              {showEmpty && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-gray-500">
                    {intl.formatMessage({ id: emptyMessageKey })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </WrapperElement>
    </>
  );
};

export default TableNew;